"use client";
import { postData } from "@/api/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import ErrorMsg from "../common/error-msg";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const schema = yup.object().shape({
  name: yup.string().required().label("Name"),
  email: yup.string().required().email().label("Email"),
  subject: yup.string().required().min(5).label("Subject"),
  message: yup.string().required().label("Message"),
});

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);
      const resp = await postData(`/contacts`, data);

      setToastMessage(resp?.message || "Something went wrong");
      if (resp?.success) reset();
    } catch (error) {
      setToastMessage("Failed to send message");
    } finally {
      setLoading(false);
      setTimeout(() => setToastMessage(""), 4000);
    }
  });

  return (
    <div className="gift-form-card">
      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="field half">
            <label>Name *</label>
            <input {...register("name")} placeholder="Your Name" />
            <ErrorMsg msg={errors.name?.message!} />
          </div>

          <div className="field half">
            <label>Email *</label>
            <input {...register("email")} placeholder="Your Email" />
            <ErrorMsg msg={errors.email?.message!} />
          </div>
        </div>

        <div className="field">
          <label>Subject *</label>
          <input {...register("subject")} placeholder="Subject" />
          <ErrorMsg msg={errors.subject?.message!} />
        </div>

        <div className="field">
          <label>Message *</label>
          <textarea {...register("message")} rows={5} placeholder="Write your message..." />
          <ErrorMsg msg={errors.message?.message!} />
        </div>

        {toastMessage && <div className="toast">{toastMessage}</div>}

        <button type="submit" disabled={loading}>
          {loading ? "Sending..." : "🎁 Send Message"}
        </button>
      </form>

      <style jsx>{`
        .gift-form-card {
          background: #ffffff;
          border-radius: 1.25rem;
          padding: 2rem;
          border: 1px solid #ffe6dc;
          box-shadow: 0 14px 35px rgba(255, 107, 107, 0.12);
          transition: all 0.3s ease;
        }

        .gift-form-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 22px 45px rgba(255, 107, 107, 0.18);
        }

        form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .row {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .field {
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .half {
          min-width: 220px;
        }

        label {
          font-size: 0.85rem;
          font-weight: 700;
          margin-bottom: 0.35rem;
          color: #374151;
        }

        input,
        textarea {
          padding: 12px 14px;
          border-radius: 10px;
          border: 1px solid #ffd8cc;
          background: #fffaf7;
          font-size: 0.9rem;
          transition: all 0.25s ease;
        }

        input:focus,
        textarea:focus {
          outline: none;
          border-color: #ff6b6b;
          box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.15);
          background: #fff;
        }

        button {
          margin-top: 0.5rem;
          padding: 14px;
          border-radius: 999px;
          border: none;
          background: linear-gradient(135deg, #ff6b6b, #ff8e53);
          color: #fff;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(255, 107, 107, 0.4);
        }

        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .toast {
          padding: 10px;
          border-radius: 8px;
          background: #22c55e;
          color: #fff;
          text-align: center;
          font-size: 0.85rem;
        }

        /* Mobile */
        @media (max-width: 640px) {
          .gift-form-card {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactForm;
